import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Upload } from 'antd';
import { connect } from 'react-redux';
import actions from '@flux/action';
import useUpload from './useUpload';
import { API, Downloader } from '@service';
import { ValidationType } from '@utils/excelValidator';
import { formatFinancialUploadhistory } from '@utils/widget';
import { isArray, isNumber } from 'lodash';

const sheetRule = {
  rules: [
    {
      col: 'A',
      rule: ValidationType.NonBlank,
    },
    {
      col: 'B',
      rule: [ValidationType.NonBlank, ValidationType.NonDuplicate],
    },
    {
      col: 'C',
      validator: (c, p, _, cellsOfSheet) => {
        const shouldAppearIn = cellsOfSheet[1];
        const cellValue = c?.value;
        if (
          cellValue &&
          (isNumber(cellValue) ||
            (typeof cellValue === 'string' && cellValue.length > 0)) &&
          !shouldAppearIn.includes(cellValue)
        ) {
          return `The parent duns number in cell ${p} does not exist.`;
        }
        return true;
      },
    },
    {
      col: 'D',
      rule: ValidationType.NonBlank,
    },
    {
      col: 'E',
      rule: ValidationType.NonBlank,
    },
    {
      col: 'G',
      rule: ValidationType.Numeric,
    },
    {
      col: 'H',
      rule: ValidationType.Numeric,
    },
    {
      col: 'D',
      validator: (vals, sheetName) => {
        const count = vals.filter((v) => v === 'Headquarters').length;
        if (vals.length === 0) {
          return true;
        }
        if (count === 1) {
          return true;
        }
        if (count === 0) {
          return `No Headquarters record on ${sheetName}`;
        }
        return `There should only exist one Headquarters record on ${sheetName}`;
      },
      type: 'col',
    },
  ],
  header: 'A4:H4',
};

const officeSheetRule = {
  rules: [
    {
      col: 'B',
      rule: [ValidationType.NonBlank, ValidationType.Boolean],
    },
    {
      col: 'B',
      validator: (vals, sheetName) => {
        const count = vals.filter((v) => v === 'TRUE' || v === true).length;
        if (vals.length === 0) {
          return true;
        }
        if (count <= 1) {
          return true;
        }
        return `There should be only one headquarter for office location on ${sheetName}`;
      },
      type: 'col',
    },
    {
      col: 'C',
      rule: ValidationType.NonBlank,
    },
    {
      col: 'E',
      rule: ValidationType.NonBlank,
    },
    {
      col: 'G',
      rule: ValidationType.NonBlank,
    },
    {
      col: 'H',
      rule: [
        ValidationType.NonBlank,
        ValidationType.Numeric,
        ValidationType.Latitude,
      ],
    },
    {
      col: 'I',
      rule: [
        ValidationType.NonBlank,
        ValidationType.Numeric,
        ValidationType.Longitude,
      ],
    },
  ],
  header: 'A4:I4',
};

const rules = {
  'A - LE Presence Current State': sheetRule,
  'A - LE Presence Future State': sheetRule,
  'B - LE Presence Current State': sheetRule,
  'B - LE Presence Future State': sheetRule,
  'A - Office Current State': officeSheetRule,
  'A - Office Future State': officeSheetRule,
  'B - Office Current State': officeSheetRule,
  'B - Office Future State': officeSheetRule,
};

const UploadExcelModal = ({
  shouldShow,
  closeModal,
  projectId,
  toggleLoading,
  onComplete,
  type = 'LE',
  keepHistory = true,
}) => {
  const [historyList, setHistoryList] = useState([]);

  const fetchHistory = useCallback(
    () =>
      API.getLEUploadHistory(projectId).then(
        (data) => isArray(data) && setHistoryList(data),
      ),
    [projectId],
  );

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  useEffect(() => {
    if (!keepHistory) {
      setHistoryList([]);
    }
  }, [keepHistory]);

  const renderIfHasHistory = (ele) =>
    historyList.length > 0 ? (
      ele
    ) : (
      <div className="no-data">No History Found</div>
    );

  const validator = (wb) => {
    const sheet = wb.getWorksheet('A - LE Presence Current State');
    if (!sheet) {
      return 'Sheet "A - LE Presence Current State" not found.';
    }
    const cellValue = sheet.getCell('A2').value;
    if (cellValue === projectId) {
      return true;
    }
    return 'The data you uploaded is not for this project.';
  };

  const { file, onUpload, clear, badge, errorInfo, isSuccess } = useUpload(
    rules,
    validator,
  );

  const uploadExcel = (formData) => API.uploadLESubExcel(projectId, formData);

  const onOk = async () => {
    toggleLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', 'le-subs.xlsx');
    await uploadExcel(formData);
    closeModal();
    toggleLoading(false);
    clear();
    fetchHistory();
    onComplete();
  };

  const onCancel = () => {
    closeModal();
    clear();
  };

  const genHistoryText = ({ uploadedAt, providedBy }) =>
    formatFinancialUploadhistory(new Date(uploadedAt), providedBy);

  const exportExcel = async () => {
    toggleLoading(true);
    await Downloader.getFile(
      `/project/subsidiary/${projectId}/report?type=${type}`,
    );
    toggleLoading(false);
  };

  return (
    <Modal
      visible={shouldShow}
      className="upload-excel-le-modal"
      title="Edit Data"
      onCancel={onCancel}
      okButtonProps={{ disabled: !isSuccess }}
      onOk={onOk}
      destroyOnClose
      zIndex={20}
      okText="Ok"
    >
      <div className="le-upload-modal-body-left">
        <Upload
          beforeUpload={onUpload}
          showUploadList={false}
          accept=".xlsx,.xls"
          className="upload-button"
        >
          <span className="appkiticon icon-upload-fill" />
          <span>import</span>
        </Upload>
        <div className="download-button" onClick={exportExcel}>
          <div className="icon-and-text">
            <span className="appkiticon icon-download-fill" />
            <span>export</span>
          </div>
        </div>
        {shouldShow ? (
          <div className="info-area">
            {badge}
            {errorInfo}
          </div>
        ) : null}
      </div>
      <div className="le-upload-modal-body-right">
        <div className="upload-history">
          <span>upload history</span>
          <div className="history-list">
            {renderIfHasHistory(
              historyList
                .filter(({ providedBy }) => providedBy !== null)
                .map(genHistoryText)
                .map((t, i) => <p key={i}>{t}</p>),
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default connect(
  ({ analysis, common }) => ({
    shouldShow: analysis.showUploadLEModal,
    projectId: common.currentId,
  }),
  (dispatch) => ({
    closeModal() {
      dispatch(actions.toggleLeUploadModal(false));
    },
    toggleLoading(bool) {
      dispatch(actions.changeLoading(bool));
    },
  }),
)(UploadExcelModal);
